import { useRef, useState } from "react";
import { Divider, ListSubheader, Menu, MenuItem } from "@mui/material";
import SvgIcon from "../../helpers/SvgIcon";
import { firstLetterToUpperCase } from "../../helpers/stringFormater";

type Props = {
  username?: string | null;
  email?: string | null;
  onLogout: () => void;
};

export default function NavbarAccountMenu({
  username,
  email,
  onLogout,
}: Props) {
  const displayName =
    firstLetterToUpperCase(username?.trim()) || email?.trim() || "Utilizator";
  const nameParts = displayName.split(/\s+/);
  const initials = (
    nameParts[0][0] +
    (nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : "")
  ).toLocaleUpperCase("ro");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);

  const avatar = (large = false) => (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-brand-subtle font-semibold text-[#2f6b98] ${
        large ? "h-[38px] w-[38px] text-base" : "h-[30px] w-[30px] text-sm"
      }`}>
      {initials}
    </span>
  );

  return (
    <>
      <button
        ref={triggerRef}
        id="navbar-account-trigger"
        type="button"
        aria-label={`Cont utilizator: ${displayName}`}
        aria-haspopup="menu"
        aria-controls={open ? "navbar-account-menu" : undefined}
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
          }
        }}
        className={`inline-flex h-[44px] max-w-full cursor-pointer items-center gap-2 rounded-control border-0 py-1.5 pl-2 pr-3 text-left text-[#3f4254] transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 sm:gap-3 ${
          open ? "bg-slate-100" : "bg-surface-subtle"
        }`}>
        {avatar()}
        <span className="hidden max-w-[180px] truncate text-base font-semibold md:block">
          {displayName}
        </span>
        <SvgIcon
          type="EXPAND_MORE"
          fill="currentColor"
          className={`!h-5 !w-5 shrink-0 text-muted-dark transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <Menu
        id="navbar-account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          "aria-labelledby": "navbar-account-trigger",
          sx: { p: 0.75 },
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            width: 280,
            maxWidth: "calc(100vw - 24px)",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            boxShadow: "0 8px 28px rgba(24, 28, 50, 0.12)",
          },
        }}
        TransitionProps={{ onExited: () => triggerRef.current?.focus() }}>
        <ListSubheader
          role="presentation"
          className="!static !flex !items-center !gap-3 !bg-transparent !px-3 !py-3 !leading-normal">
          {avatar(true)}
          <div className="min-w-0">
            <p className="m-0 break-words text-base font-semibold leading-snug text-slate-800">
              {displayName}
            </p>
            <p className="mb-0 mt-0.5 text-sm font-normal text-muted-dark">
              Cont client
            </p>
            {email?.trim() && email.trim() !== displayName && (
              <p className="mb-0 mt-1 break-words text-[12px] text-slate-500">
                {email.trim()}
              </p>
            )}
          </div>
        </ListSubheader>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            closeMenu();
            onLogout();
          }}
          className="!min-h-[44px] !gap-3 !rounded-control !px-3 !text-slate-700 hover:!bg-surface-subtle">
          <SvgIcon
            type="LOGOUT_ICON"
            fill="currentColor"
            className="!h-5 !w-5 text-muted-dark"
          />
          Deconectare
        </MenuItem>
      </Menu>
    </>
  );
}
